// Message List Component
// Displays all messages in a conversation with auto-scroll and scroll intelligence

import { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2, ChevronDown } from 'lucide-react';
import { MessageItem } from './MessageItem';
import { MarkdownContent } from './MarkdownContent';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useChatStore } from '@/stores/chatStore';
import { createComponentLogger } from '@/utils/logger';
import { cn } from '@/lib/utils';
import type { AIMessage, CitationMetadata } from '@/types/chat';

const log = createComponentLogger('MessageList', 'ai-chat');

// Threshold in pixels for considering user "at bottom"
const SCROLL_BOTTOM_THRESHOLD = 100;

// ============================================================================
// Component Props
// ============================================================================

interface MessageListProps {
  conversationId: string;
  messages: AIMessage[];
  isLoading: boolean;
  /** Callback when a citation is clicked */
  onSelectCitation?: (citation: CitationMetadata) => void;
  /** Callback when regenerate is clicked (receives message ID) */
  onRegenerate?: (messageId: string) => void;
  /** Whether regeneration is allowed for this conversation */
  allowRegenerate?: boolean;
}

// Helper to find the nearest scrollable parent
function findScrollableParent(element: HTMLElement | null): HTMLElement | null {
  if (!element) {
return null;
}

  let parent = element.parentElement;
  while (parent) {
    const { overflow, overflowY } = getComputedStyle(parent);
    if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

// ============================================================================
// Main Component
// ============================================================================

export function MessageList({ conversationId, messages, isLoading, onSelectCitation, onRegenerate, allowRegenerate = true }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef(messages.length);
  const streamingMessage = useChatStore((state) => state.streamingMessage);
  const isSending = useChatStore((state) => state.isSending);

  // Scroll intelligence state
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null);

  // Loading state tracking
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const sendStartTimeRef = useRef<number | null>(null);

  // Find scrollable parent on mount
  useEffect(() => {
    const parent = findScrollableParent(containerRef.current);
    if (parent) {
      setScrollContainer(parent);
      log.debug('Found scrollable parent', { tagName: parent.tagName });
    }
  }, []);

  // Track elapsed time when sending
  useEffect(() => {
    if (isSending) {
      sendStartTimeRef.current = Date.now();
      setElapsedSeconds(0);

      const interval = setInterval(() => {
        if (sendStartTimeRef.current) {
          setElapsedSeconds(Math.floor((Date.now() - sendStartTimeRef.current) / 1000));
        }
      }, 1000);

      return () => clearInterval(interval);
    } else {
      sendStartTimeRef.current = null;
      setElapsedSeconds(0);
    }
  }, [isSending]);

  // Check if this conversation has an active streaming message
  const hasStreamingMessage = streamingMessage && streamingMessage.conversation_id === conversationId;

  // Determine the loading phase based on elapsed time and streaming state
  const getLoadingPhase = (): { text: string; showTime: boolean } => {
    if (hasStreamingMessage && !streamingMessage.content) {
      return { text: 'Searching documents...', showTime: true };
    }
    if (elapsedSeconds < 3) {
      return { text: 'Thinking...', showTime: false };
    }
    if (elapsedSeconds < 8) {
      return { text: 'Searching documents...', showTime: true };
    }
    if (elapsedSeconds < 15) {
      return { text: 'Analyzing information...', showTime: true };
    }
    return { text: 'Still working...', showTime: true };
  };

  // ============================================================================
  // Scroll Helpers
  // ============================================================================

  const checkIfAtBottom = useCallback(() => {
    if (!scrollContainer) {
return true;
}

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    return scrollHeight - scrollTop - clientHeight < SCROLL_BOTTOM_THRESHOLD;
  }, [scrollContainer]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
    setNewMessageCount(0);
    setIsAtBottom(true);
  }, []);

  // ============================================================================
  // Effects
  // ============================================================================

  // Track scroll position
  useEffect(() => {
    if (!scrollContainer) {
return;
}

    const handleScroll = () => {
      const atBottom = checkIfAtBottom();
      setIsAtBottom(atBottom);

      // Clear new message count when user scrolls to bottom
      if (atBottom) {
        setNewMessageCount(0);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [scrollContainer, checkIfAtBottom]);

  // Smart auto-scroll when new messages arrive
  useEffect(() => {
    const newCount = messages.length - lastMessageCountRef.current;

    if (newCount > 0) {
      if (isAtBottom) {
        // User is at bottom - auto-scroll smoothly
        scrollToBottom('smooth');
        log.debug('Auto-scrolled to new message', {
          conversationId,
          messageCount: messages.length
        });
      } else {
        // User scrolled up - show indicator instead
        setNewMessageCount(prev => prev + newCount);
        log.debug('New message arrived while scrolled up', {
          conversationId,
          newCount,
          totalNew: newMessageCount + newCount
        });
      }
    }

    lastMessageCountRef.current = messages.length;
  }, [messages.length, conversationId, isAtBottom, scrollToBottom, newMessageCount]);

  // Auto-scroll during streaming if at bottom
  useEffect(() => {
    if (hasStreamingMessage && isAtBottom) {
      // Use instant scroll during streaming for smoother experience
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
    }
  }, [streamingMessage?.content, hasStreamingMessage, isAtBottom]);

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const renderLoadingSkeleton = () => (
    <div className="flex gap-3 mb-6">
      <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
      <div className="flex-1 max-w-[75%] space-y-2">
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-4 w-24" />
      </div>
    </div>
  );

  const renderThinkingIndicator = () => {
    const phase = getLoadingPhase();

    return (
      <div className="flex gap-3 mb-6 animate-in fade-in-50 slide-in-from-bottom-2">
        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
          AI
        </div>
        <div className="flex-1 max-w-[75%]">
          <div className="bg-gradient-to-br from-muted/80 to-muted/60 p-4 rounded-xl shadow-sm backdrop-blur-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">{phase.text}</span>
              {phase.showTime && elapsedSeconds > 0 && (
                <span className="text-xs text-muted-foreground/60 tabular-nums">
                  {elapsedSeconds}s
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStreamingMessage = () => {
    if (!hasStreamingMessage) {
return null;
}

    const phase = getLoadingPhase();

    return (
      <div className="flex gap-3 mb-6 animate-in fade-in-50 slide-in-from-bottom-2">
        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
          AI
        </div>
        <div className="flex-1 max-w-[75%]">
          <div className="relative bg-gradient-to-br from-muted/80 to-muted/60 p-4 rounded-xl shadow-sm border border-border/50 overflow-hidden">
            {/* Subtle pulsing glow effect during streaming */}
            {!streamingMessage.content && (
              <div className="absolute inset-0 bg-primary/5 animate-pulse" />
            )}
            {streamingMessage.content ? (
              <>
                <MarkdownContent content={streamingMessage.content} />
                <span className="inline-block w-1.5 h-3.5 bg-primary/70 ml-0.5 animate-pulse" />
              </>
            ) : (
              <div className="relative flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span className="text-[13px]">{phase.text}</span>
                {phase.showTime && elapsedSeconds > 0 && (
                  <span className="text-xs text-muted-foreground/60 tabular-nums">
                    {elapsedSeconds}s
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center h-full text-center p-8">
      {/* Icon with gradient glow effect */}
      <div className="relative mb-6">
        <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full animate-pulse" />
        <div className="relative text-6xl">💬</div>
      </div>

      {/* Gradient heading */}
      <h3 className="text-lg font-semibold bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
        Start a conversation
      </h3>
    </div>
  );

  const renderJumpToLatest = () => {
    // Only show when user has scrolled up and there are new messages or not at bottom
    if (isAtBottom && newMessageCount === 0) {
return null;
}

    return (
      <div className="sticky bottom-4 flex justify-center pointer-events-none">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => scrollToBottom('smooth')}
          className={cn(
            'pointer-events-auto shadow-lg border border-border/50',
            'animate-in fade-in-50 slide-in-from-bottom-2',
            'hover:shadow-xl transition-all duration-200',
            'flex items-center gap-1.5 px-3 h-8 rounded-full'
          )}
        >
          <ChevronDown className="h-3.5 w-3.5" />
          <span className="text-xs font-medium">
            {newMessageCount > 0 ? (
              <>
                {newMessageCount} new message{newMessageCount > 1 ? 's' : ''}
              </>
            ) : (
              'Jump to latest'
            )}
          </span>
          {newMessageCount > 0 && (
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
          )}
        </Button>
      </div>
    );
  };

  // ============================================================================
  // Render
  // ============================================================================

  // Empty state
  if (messages.length === 0 && !isLoading) {
    return renderEmptyState();
  }

  return (
    <div ref={containerRef} className="relative py-6">
      {/* Existing Messages */}
      {messages.map((message, index) => (
        <div key={message.id} className="animate-in fade-in-50 slide-in-from-bottom-2" style={{ animationDelay: `${index * 50}ms`, animationDuration: '300ms' }}>
          <MessageItem
            message={message}
            onSelectCitation={onSelectCitation}
            onRegenerate={onRegenerate ? () => onRegenerate(message.id) : undefined}
            allowRegenerate={allowRegenerate && message.role === 'assistant'}
          />
        </div>
      ))}

      {/* Streaming Message (real-time response) */}
      {renderStreamingMessage()}

      {/* Thinking Indicator (for non-streaming requests) */}
      {isSending && !hasStreamingMessage && renderThinkingIndicator()}

      {/* Jump to latest floating pill */}
      {renderJumpToLatest()}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />
    </div>
  );
}
